import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  SwipeableDrawer,
  Typography,
  useTheme,
} from "@mui/material";
import { CloseRounded } from "@mui/icons-material";
import { AuroraGlow } from "../auth/AuroraGlow";
import { overlaySurface } from "../Dashboard/overviewPanel";

/**
 * The paywall's exit-intent sheet: one honest discount on the yearly term,
 * raised when the user goes to leave the hard paywall (NativePaywall owns the
 * triggers and the eligibility guard). Dismissing it is not final — it rises
 * again on the next exit attempt; "once" belongs to the PRICE, which Apple
 * enforces via intro-offer eligibility, not to the sheet. Purely
 * presentational — every price string arrives already localized from store
 * data, never hardcoded, so this surface can't contradict the payment sheet.
 *
 * Copy follows the news-service humaniser rules (packages/news-service/src/
 * ai/humaniser/prompt.ts): no em-dashes, no reflexive tricolons, varied
 * sentence length. The framing cashes what onboarding already earned — the
 * step-3 commitment screen ("Most people guess when buying stocks" answered
 * with "I'm ready") — so leaving reads as going back to guessing. No
 * countdown, no invented urgency: "once" is literally true via the
 * once-ever device flag.
 *
 * Three honest ways out (close button, swipe-down, "Not now") keep this an
 * offer rather than a trap, which is also the App Review posture.
 */

interface ExitOfferSheetProps {
  open: boolean;
  busy: boolean;
  /** Localized per-month figure for the first year, e.g. "£5.00". */
  perMonthPrice: string;
  /** The standard yearly per-month figure it undercuts, e.g. "£6.25". */
  standardPerMonthPrice: string;
  /** Localized first-year charge, e.g. "£59.99". */
  introTotal: string;
  /** Localized renewal sticker after the first year, e.g. "£74.99". */
  renewalTotal: string;
  /** Saving vs paying monthly, whole percent — derived, never hardcoded. */
  savingPercent: number;
  onAccept: () => void;
  onDismiss: () => void;
}

export const ExitOfferSheet = ({
  open,
  busy,
  perMonthPrice,
  standardPerMonthPrice,
  introTotal,
  renewalTotal,
  savingPercent,
  onAccept,
  onDismiss,
}: ExitOfferSheetProps) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";

  return (
    <SwipeableDrawer
      anchor="bottom"
      open={open}
      onClose={onDismiss}
      onOpen={() => {}}
      disableSwipeToOpen
      disableBackdropTransition
      disableDiscovery
      // Lighter than the standard scrim so the paywall stays visible behind:
      // the sheet must read as an offer ON the paywall, not a new screen
      // (same rationale as ChatCapabilitiesSheet's backdrop).
      slotProps={{
        backdrop: { sx: { backgroundColor: "rgba(0, 0, 0, 0.35)" } },
      }}
      PaperProps={{
        sx: {
          borderTopLeftRadius: 24,
          borderTopRightRadius: 24,
          backgroundColor: overlaySurface(isDark),
          backgroundImage: "none",
          boxShadow: "none",
          // Clips the aurora field to the rounded top corners.
          overflow: "hidden",
          px: 2.5,
          pt: 1.5,
          pb: "calc(env(safe-area-inset-bottom, 0px) + 8px)",
        },
      }}
    >
      {/* The onboarding's aurora field runs behind the offer, so the sheet
          reads as part of the same world as the quiz and the paywall rather
          than a flat grey interruption. */}
      <AuroraGlow />

      <IconButton
        aria-label="Close"
        onClick={onDismiss}
        sx={{
          position: "absolute",
          top: 10,
          right: 10,
          color: isDark ? "#9ca3af" : "#64748b",
        }}
      >
        <CloseRounded sx={{ fontSize: 19 }} />
      </IconButton>

      <Box sx={{ position: "relative" }}>
      {/* Grab handle */}
      <Box sx={{ display: "flex", justifyContent: "center", mb: 2 }}>
        <Box
          sx={{
            width: 36,
            height: 4,
            borderRadius: 2,
            backgroundColor: isDark ? "rgba(255,255,255,0.2)" : "rgba(0,0,0,0.2)",
          }}
        />
      </Box>

      <Typography
        sx={{
          fontSize: "0.6875rem",
          fontWeight: 700,
          letterSpacing: "0.08em",
          textTransform: "uppercase",
          color: isDark ? "#4ade80" : "#16a34a",
        }}
      >
        Before you go
      </Typography>
      <Typography
        sx={{
          mt: 0.5,
          fontWeight: 800,
          fontSize: "1.5rem",
          lineHeight: 1.15,
          letterSpacing: "-0.02em",
          color: isDark ? "#ffffff" : "#1d1d1f",
        }}
      >
        Don&rsquo;t go back to guessing.
      </Typography>
      <Typography
        sx={{
          mt: 1,
          fontSize: "0.9375rem",
          lineHeight: 1.5,
          color: isDark ? "rgba(255,255,255,0.92)" : "#374151",
        }}
      >
        Your next trade is real money. Keep the data professionals use, and
        the AI that reads it for you.
      </Typography>

      {/* The one-time-ness IS the offer — it gets its own line, in the
          highlight green, directly above the price it describes. */}
      <Typography
        sx={{
          mt: 2.5,
          fontSize: "1.0625rem",
          fontWeight: 800,
          letterSpacing: "-0.01em",
          color: isDark ? "#4ade80" : "#16a34a",
        }}
      >
        Our best price, once.
      </Typography>

      {/* The offer, in the paywall's own plan-row grammar (already selected —
          there is no other option to weigh). */}
      <Box
        sx={{
          mt: 1.25,
          position: "relative",
          p: 1.75,
          borderRadius: 3,
          border: "1.5px solid #22c55e",
          backgroundColor: "rgba(34, 197, 94, 0.12)",
        }}
      >
        <Typography
          sx={{
            position: "absolute",
            top: -12,
            right: 12,
            px: 0.875,
            py: 0.25,
            borderRadius: 999,
            fontSize: "0.625rem",
            fontWeight: 800,
            lineHeight: 1.6,
            letterSpacing: "0.06em",
            textTransform: "uppercase",
            whiteSpace: "nowrap",
            color: "#14532d",
            backgroundColor: "#4ade80",
          }}
        >
          Save {savingPercent}%
        </Typography>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography
              sx={{
                fontWeight: 700,
                fontSize: "0.9375rem",
                color: isDark ? "#ffffff" : "#1d1d1f",
              }}
            >
              Insight Pro &middot; Yearly
            </Typography>
            <Typography
              sx={{
                mt: 0.25,
                fontSize: "0.75rem",
                lineHeight: 1.4,
                color: isDark ? "#9ca3af" : "#64748b",
              }}
            >
              {introTotal} for your first year, then {renewalTotal}
            </Typography>
          </Box>
          <Box sx={{ textAlign: "right", flexShrink: 0 }}>
            <Typography
              sx={{
                fontSize: "0.75rem",
                color: isDark ? "#6b7280" : "#9ca3af",
                textDecoration: "line-through",
              }}
            >
              {standardPerMonthPrice}
            </Typography>
            <Typography
              sx={{
                fontWeight: 800,
                fontSize: "1.1875rem",
                lineHeight: 1.15,
                letterSpacing: "-0.02em",
                color: "#22c55e",
              }}
            >
              {perMonthPrice}
              <Typography
                component="span"
                sx={{
                  fontSize: "0.75rem",
                  fontWeight: 500,
                  letterSpacing: 0,
                  color: isDark ? "#9ca3af" : "#64748b",
                }}
              >
                {" "}
                /mo
              </Typography>
            </Typography>
          </Box>
        </Box>
      </Box>

      <Button
        fullWidth
        disabled={busy}
        onClick={onAccept}
        sx={{
          mt: 2,
          py: 1.2,
          backgroundColor: "#22c55e",
          color: "#ffffff",
          fontWeight: 600,
          fontSize: "0.9375rem",
          textTransform: "none",
          borderRadius: 2,
          "&:hover": { backgroundColor: "#16a34a" },
          "&:disabled": {
            backgroundColor: isDark ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.1)",
          },
        }}
      >
        {busy && <CircularProgress size={22} color="inherit" />}
        {!busy && `Get a year for ${introTotal}`}
      </Button>

      <Typography
        sx={{
          mt: 1.5,
          textAlign: "center",
          fontSize: "0.6875rem",
          lineHeight: 1.5,
          color: isDark ? "#6b7280" : "#9ca3af",
        }}
      >
        One-time offer for new members. Renews at the standard price until
        cancelled.
      </Typography>

      <Button
        fullWidth
        variant="text"
        onClick={onDismiss}
        disabled={busy}
        sx={{
          mt: 0.5,
          py: 1,
          fontWeight: 600,
          fontSize: "0.8125rem",
          textTransform: "none",
          borderRadius: 2,
          color: isDark ? "#9ca3af" : "#64748b",
        }}
      >
        Not now
      </Button>
      </Box>
    </SwipeableDrawer>
  );
};
